import * as Phaser from "phaser"
import { EventBus } from "../core/event-bus"
import { EVENT } from "../core/keys"
import {
  BUMPER_OVERDRIVE_EFFECT,
  getBumperCharge,
} from "../data/bumper-feedback"
import { BASE_BOUNCE_FORCE, GAME_WIDTH, SCENE_TUNING } from "../data/constants"
import type { BumperEffectType, GameData, GameSprite } from "../types"
import { type BumperBonusLevel, BumperEffects } from "./bumper-effects"
import type { BumperSystem } from "./bumper-system"
import type { VfxManager } from "./vfx-manager"

const BUMPER_POP_FORCE_MULT = 1.65
const BUMPER_POP_INCOMING_CARRY = 0.28
const SLINGSHOT_LAUNCH_MULT = 2.4

interface BumperHitsOptions {
  scene: Phaser.Scene
  vfx: VfxManager
  enemyGroup: Phaser.Physics.Arcade.Group
  bumperSystem: BumperSystem
  getGameData: () => GameData
  getDogSprite: () => GameSprite
  onBumperHitSound: () => void
  onActivatePower: (effect: BumperEffectType) => void
  onDefeatEnemy: (enemy: GameSprite) => void
  onDamageBossEnemy: (enemy: GameSprite, ratio: number, color: number) => void
  spawnCoins: (x: number, y: number, value: number) => void
  healPlayer: (amount: number) => void
  grantXp: (amount: number) => void
}

export class BumperHits {
  private readonly options: BumperHitsOptions
  private readonly effects: BumperEffects

  constructor(options: BumperHitsOptions) {
    this.options = options
    this.effects = new BumperEffects({
      scene: options.scene,
      vfx: options.vfx,
      enemyGroup: options.enemyGroup,
      onDefeatEnemy: options.onDefeatEnemy,
      onDamageBossEnemy: options.onDamageBossEnemy,
      spawnCoins: options.spawnCoins,
      healPlayer: options.healPlayer,
      grantXp: options.grantXp,
    })
  }

  public handleHit(bumperSprite: GameSprite): void {
    const bd = bumperSprite.entityData
    if (!bd || bd.kind !== "bumper") return

    const { bumperSystem, vfx, getGameData, getDogSprite, onBumperHitSound } =
      this.options
    const gameData = getGameData()
    const dogSprite = getDogSprite()

    bumperSystem.registerPlayerContact(bumperSprite)
    onBumperHitSound()
    gameData.totalBumperHits++

    const body = dogSprite.body as Phaser.Physics.Arcade.Body

    if (bd.slingshotAngle !== undefined) {
      this.handleSlingshotLaunch(
        bumperSprite,
        bd,
        dogSprite,
        body,
        gameData,
        vfx,
      )
      return
    }

    this.handleBumperPop(bumperSprite, bd, dogSprite, body, gameData, vfx)
  }

  // ── Slingshot branch ──────────────────────────────────────────

  private handleSlingshotLaunch(
    bumperSprite: GameSprite,
    bd: Extract<GameSprite["entityData"], { kind: "bumper" }>,
    dogSprite: GameSprite,
    body: Phaser.Physics.Arcade.Body,
    gameData: GameData,
    vfx: VfxManager,
  ): void {
    // When the hero touches a slingshot deflector, they are flung in the
    // slingshot's preset kick direction (like a pinball flipper launch).
    // This is exciting and rewarding, NOT punishing — it grants brief
    // invulnerability and a strong velocity boost toward the enemy field.
    const launchAngle = bd.slingshotAngle ?? 0
    const launchSpeed =
      SCENE_TUNING.bumperPlayerMinLaunchSpeed * SLINGSHOT_LAUNCH_MULT
    const kickVx = Math.cos(launchAngle) * launchSpeed
    const kickVy = Math.sin(launchAngle) * launchSpeed

    const dx = dogSprite.x - bumperSprite.x
    const dy = dogSprite.y - bumperSprite.y
    const dist = Math.max(1, Math.hypot(dx, dy))
    const sep = bd.radius + SCENE_TUNING.bumperPlayerLaunchSeparation + 8
    dogSprite.setPosition(
      Phaser.Math.Clamp(
        bumperSprite.x + (dx / dist) * sep,
        SCENE_TUNING.playerBoundsMarginX,
        GAME_WIDTH - SCENE_TUNING.playerBoundsMarginRight,
      ),
      Phaser.Math.Clamp(
        bumperSprite.y + (dy / dist) * sep,
        SCENE_TUNING.playerTopBoundaryY,
        SCENE_TUNING.playerBottomBoundaryY,
      ),
    )

    body.setVelocity(kickVx, kickVy)
    dogSprite.angle = Phaser.Math.Clamp(kickVy / 20, -25, 25)

    // Brief invulnerability — the launch is powerful, not harmful
    if (!gameData.player.isInvulnerable) {
      gameData.player.isInvulnerable = true
      gameData.player.invulnerabilityTimer =
        SCENE_TUNING.bumperSafetyInvulnerabilityMs
      dogSprite.setTint(0x84cc16)
      this.options.scene.time.delayedCall(
        SCENE_TUNING.bumperSafetyInvulnerabilityMs,
        () => {
          dogSprite.clearTint()
        },
      )
    }

    vfx.bumperEmitter.explode(36, bumperSprite.x, bumperSprite.y)
    vfx.emitBumperRewardPulse(bumperSprite.x, bumperSprite.y, 0x84cc16, 1.6)
    vfx.shakeCamera(70, 0.006)

    EventBus.emit(EVENT.BUMPER_HIT)
  }

  // ── Regular bumper branch ─────────────────────────────────────

  private handleBumperPop(
    bumperSprite: GameSprite,
    bd: Extract<GameSprite["entityData"], { kind: "bumper" }>,
    dogSprite: GameSprite,
    body: Phaser.Physics.Arcade.Body,
    gameData: GameData,
    vfx: VfxManager,
  ): void {
    // Real pinball bumpers fire the ball (hero) away at a FIXED pop speed —
    // direction is away from bumper center, speed is always the same pop value
    // regardless of how fast or slow the hero was moving when they arrived.
    const dx = dogSprite.x - bumperSprite.x
    const dy = dogSprite.y - bumperSprite.y
    const distance = Math.max(1, Math.hypot(dx, dy))
    let normalX = dx / distance
    let normalY = dy / distance

    if (distance <= 1.0) {
      // Hero is exactly on bumper center — push using incoming velocity direction
      const vlen = Math.max(1, Math.hypot(body.velocity.x, body.velocity.y))
      normalX = body.velocity.x !== 0 ? -body.velocity.x / vlen : -1
      normalY = body.velocity.y !== 0 ? -body.velocity.y / vlen : 0
    }

    const popSpeed =
      Math.max(BASE_BOUNCE_FORCE, SCENE_TUNING.bumperPlayerMinLaunchSpeed) *
      BUMPER_POP_FORCE_MULT
    const incomingSpeed = Math.hypot(body.velocity.x, body.velocity.y)
    const exitSpeed = popSpeed + incomingSpeed * BUMPER_POP_INCOMING_CARRY

    const sep = bd.radius + SCENE_TUNING.bumperPlayerLaunchSeparation + 4
    dogSprite.setPosition(
      Phaser.Math.Clamp(
        bumperSprite.x + normalX * sep,
        SCENE_TUNING.playerBoundsMarginX,
        GAME_WIDTH - SCENE_TUNING.playerBoundsMarginRight,
      ),
      Phaser.Math.Clamp(
        bumperSprite.y + normalY * sep,
        SCENE_TUNING.playerTopBoundaryY,
        SCENE_TUNING.playerBottomBoundaryY,
      ),
    )
    body.setVelocity(normalX * exitSpeed, normalY * exitSpeed)

    const comboProgress =
      (gameData.player.combo.hits + 1) /
      Math.max(1, gameData.player.combo.targetHits)
    const bumperColor = Phaser.Display.Color.HexStringToColor(bd.color).color
    vfx.shakeCamera(55 + comboProgress * 35, 0.004 + comboProgress * 0.004)
    vfx.bumperEmitter.explode(
      30 + Math.round(comboProgress * 18),
      bumperSprite.x,
      bumperSprite.y,
    )
    vfx.emitBumperRewardPulse(
      bumperSprite.x,
      bumperSprite.y,
      bumperColor,
      1.35 + comboProgress * 0.45,
    )

    // Award points (with bonus from Score Multiplier passive)
    const baseMultiplier = gameData.player.scoreMultiplier || 1
    const passiveMultiplier =
      1 + (gameData.player.passives.scoreMultiplier ?? 0)
    const totalMultiplier = baseMultiplier * passiveMultiplier
    const scoreAward = Math.floor(bd.points * totalMultiplier)
    gameData.score += scoreAward
    if (scoreAward > 0) {
      vfx.showDamageNumber(
        bumperSprite.x,
        bumperSprite.y - 20,
        scoreAward,
        0xffffff,
        false,
      )
    }

    const comboCharge = getBumperCharge(bd.effect)
    const bonusLevel = this.getBumperBonusLevel(gameData, bd.color)
    const shouldTriggerPower = this.options.bumperSystem.registerComboHit(
      gameData.player.combo,
      BUMPER_OVERDRIVE_EFFECT,
      bd.color,
      comboCharge,
    )
    if (shouldTriggerPower) {
      this.options.onActivatePower(BUMPER_OVERDRIVE_EFFECT)
    }

    this.effects.apply(bumperSprite, bd.effect, dogSprite, gameData, bonusLevel)

    EventBus.emit(EVENT.BUMPER_HIT)
  }

  private getBumperBonusLevel(
    gameData: GameData,
    color: string,
  ): BumperBonusLevel {
    const matchingHits = gameData.player.combo.hitColors.filter(
      (hitColor) => hitColor === color,
    ).length
    const level = Phaser.Math.Clamp(matchingHits, 1, 5)
    return level as BumperBonusLevel
  }
}
